/**
 * useOffline Composable
 *
 * Detects online/offline status and provides:
 * - Reactive online status
 * - Event emission when connectivity changes
 * - Integration with sync service
 */

import { ref, onMounted, onUnmounted } from 'vue'

// Singleton state
const isOnline = ref(typeof navigator !== 'undefined' ? navigator.onLine : true)
const wasOffline = ref(false)

/**
 * Custom event for when app comes back online
 */
const BACK_ONLINE_EVENT = 'app:back-online'

/**
 * useOffline Composable
 */
export function useOffline() {
  let onlineHandler: () => void
  let offlineHandler: () => void

  onMounted(() => {
    if (typeof window === 'undefined') return

    onlineHandler = () => {
      console.warn('[Offline] Connection restored')
      isOnline.value = true

      if (wasOffline.value) {
        // Dispatch custom event for sync service
        window.dispatchEvent(new CustomEvent(BACK_ONLINE_EVENT))
        wasOffline.value = false
      }
    }

    offlineHandler = () => {
      console.warn('[Offline] Connection lost')
      isOnline.value = false
      wasOffline.value = true
    }

    window.addEventListener('online', onlineHandler)
    window.addEventListener('offline', offlineHandler)

    // Initial check
    isOnline.value = navigator.onLine
  })

  onUnmounted(() => {
    if (typeof window === 'undefined') return

    window.removeEventListener('online', onlineHandler)
    window.removeEventListener('offline', offlineHandler)
  })

  /**
   * Register callback for when app comes back online
   */
  function onBackOnline(callback: () => void): () => void {
    if (typeof window === 'undefined') return () => {}

    const handler = () => callback()
    window.addEventListener(BACK_ONLINE_EVENT, handler)

    // Return cleanup function
    return () => {
      window.removeEventListener(BACK_ONLINE_EVENT, handler)
    }
  }

  /**
   * Manual check of connection status
   * Useful for verifying actual API reachability
   */
  async function checkConnection(apiUrl?: string): Promise<boolean> {
    if (!navigator.onLine) {
      return false
    }

    if (!apiUrl) {
      return navigator.onLine
    }

    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000)

      const response = await fetch(`${apiUrl}/Sync/test-connection`, {
        method: 'GET',
        signal: controller.signal,
      })

      clearTimeout(timeoutId)
      return response.ok
    } catch {
      return false
    }
  }

  return {
    // State
    isOnline,
    wasOffline,

    // Actions
    onBackOnline,
    checkConnection,
  }
}
